import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, TextField } from "@mui/material";
import type { Stand } from "../models/Barrel";
import { useStands } from "../hooks/useBarrels";

interface StandSelectorProps {
	value: number | null;
	onChange: (value: number | null) => void;
	label?: string;
	fullWidth?: boolean;
	size?: "small" | "medium";
	required?: boolean;
	error?: boolean;
	helperText?: string;
	/** Adds an "All" option and takes away the clear button: a filter always has a value. */
	includeAll?: boolean;
	sx?: SxProps<Theme>;
}

const ALL_OPTION: Stand = {
	Id: 0,
	Name: "All",
	Number: null,
	MillSizeId: null,
	MillSizeName: null,
	Active: true,
};

export const StandSelector = ({
	value,
	onChange,
	label = "Stand",
	fullWidth = false,
	size = "small",
	required = false,
	error = false,
	helperText,
	includeAll = false,
	sx,
}: StandSelectorProps) => {
	const { data: stands = [], isLoading } = useStands();
	const options = includeAll ? [ALL_OPTION, ...stands] : stands;
	const selected =
		options.find((stand) => stand.Id === value) ??
		(includeAll ? ALL_OPTION : null);

	return (
		<Autocomplete
			options={options}
			value={selected}
			size={size}
			fullWidth={fullWidth}
			disabled={isLoading}
			disableClearable={includeAll}
			autoHighlight
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, current) => option.Id === current.Id}
			onChange={(_event, option) =>
				onChange(!option || option.Id === ALL_OPTION.Id ? null : option.Id)
			}
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{option.Name}
					</li>
				);
			}}
			sx={{ minWidth: 120, ...sx } as SxProps<Theme>}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					error={error}
					helperText={helperText}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
			)}
		/>
	);
};
